import React, { useCallback } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import Swal from 'sweetalert2';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { deleteFollowUp, sendNow } from './data';
import Modal from '../../../../shared/Modal';
import DeleteFollowUp from './DeleteFollowUp.jsx';
import EditFollowUp from './EditFollowUp.jsx';
import SendNow from './SendNow.jsx';

const Title = styled.th`
  display: table-cell;
  text-align: left;
  border-bottom: solid 1px ${themeGet('colors.black.20')};
  padding-bottom: 16px;
  color: ${themeGet('colors.black.80')};
  font-weight: 500;
  font-size: 14px;
`;

const StyledTr = styled.tr`
  border-bottom: solid 1px ${themeGet('colors.black.20')};
  :hover {
    background-color: white;
  }
`;

const StyledTd = styled.td`
  padding: 12px 48px 10px 24px;
  color: ${themeGet('colors.black.100')};
  font-weight: 500;
  font-size: 14px;
`;

const ActionTd = styled.td`
  text-align: right;
`;

const ActionBtn = styled.a`
  padding: 8px 0 8px 24px;
  color: ${themeGet('colors.primary.100')};
  font-weight: 600;
  font-size: 12px;

  :last-child {
    padding-right: 14px;
  }
`;

const Placeholder = styled.p`
  font-weight: 500;
  font-size: 14px;
`;

const FollowUpsTable = ({ data }) => {
  const { t } = useTranslation('send');

  const queryClient = useQueryClient();

  const mutationSideEffects = {
    onSuccess: () => {
      queryClient.invalidateQueries(['followUps']);
      Swal.close();
    },
  };

  const sendNowMutationSideEffects = {
    onSuccess: response => {
      const { id } = response.data;
      const { follow_ups } = queryClient.getQueryData(['followUps']);
      const filteredData = follow_ups.filter(el => el.id !== id);

      queryClient.setQueryData(['followUps'], { follow_ups: filteredData });
      Swal.close();
    },
  };

  const { mutate: deleteFollowUpMutation } = useMutation(
    deleteFollowUp,
    mutationSideEffects,
  );

  const { mutate: sendNowMutation } = useMutation(
    sendNow,
    sendNowMutationSideEffects,
  );

  const deleteModal = useCallback(followUpId => () => {
    Modal({
      component: (
        <DeleteFollowUp
          onCancel={() => Swal.clickCancel()}
          onConfirm={() => deleteFollowUpMutation({ followUpId })}
        />
      ),
      width: '394px',
      padding: '24px',
    });
  });

  const editModal = useCallback(followUp => () => {
    Modal({
      component: (
        <EditFollowUp followUp={followUp} onCancel={() => Swal.clickCancel()} />
      ),
      width: '394px',
      padding: '24px',
    });
  });

  const sendNowModal = useCallback(followUpId => () => {
    Modal({
      component: (
        <SendNow
          onCancel={() => Swal.clickCancel()}
          onConfirm={() => sendNowMutation({ followUpId })}
        />
      ),
      width: '394px',
      padding: '24px',
    });
  });

  const formatedDate = date => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    };

    return new Date(date).toLocaleDateString('en-US', options);
  };

  return (
    <React.Fragment>
      {data.length ? (
        <table>
          <thead>
            <tr>
              <Title colSpan={3}>{t('followUp.mainTitle')}</Title>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              return (
                <StyledTr key={i}>
                  <StyledTd>{row.subject}</StyledTd>
                  <StyledTd>{formatedDate(row.sendOn)}</StyledTd>
                  <ActionTd>
                    <ActionBtn onClick={deleteModal(row.id)}>
                      {t('followUp.deleteButton')}
                    </ActionBtn>
                    <ActionBtn onClick={editModal(row)}>
                      {t('followUp.editButton')}
                    </ActionBtn>
                    <ActionBtn onClick={sendNowModal(row.id)}>
                      {t('followUp.sendNowButton')}
                    </ActionBtn>
                  </ActionTd>
                </StyledTr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <Placeholder>{t('followUp.placeholder')}</Placeholder>
      )}
    </React.Fragment>
  );
};

export default FollowUpsTable;
